/**
 * Native/mobile helpers for Capacitor + RevenueCat.
 *
 * On web: getPlatform() returns "web" and all IAP functions no-op.
 * On iOS/Android: getPlatform() returns "ios"/"android", and initRevenueCat + buyPremium
 * hand off to the native SDK. Fill in the REVENUECAT_*_KEY values from your dashboard.
 *
 * TODO(revenuecat): Once you have accounts,
 *   1. Sign up at https://www.revenuecat.com (free)
 *   2. Add your iOS + Android apps in the RevenueCat dashboard
 *   3. Paste the "public API key" from each app into IOS_KEY / ANDROID_KEY below
 *   4. Create an "atelier_premium" entitlement in RC and attach the App Store
 *      + Play Store product IDs (both named "atelier_annual_gbp") to it
 *   5. Configure the webhook: POST to <api>/api/billing/revenuecat/webhook
 */
import { logger } from "./log";

const IOS_KEY = process.env.REACT_APP_REVENUECAT_IOS_KEY || "";
const ANDROID_KEY = process.env.REACT_APP_REVENUECAT_ANDROID_KEY || "";

export const getPlatform = () => {
  if (typeof window === "undefined") return "web";
  const cap = window.Capacitor;
  if (!cap || typeof cap.getPlatform !== "function") return "web";
  return cap.getPlatform();
};

export const isNative = () => {
  const p = getPlatform();
  return p === "ios" || p === "android";
};

let _rcReady = false;
export const initRevenueCat = async (userId) => {
  if (!isNative()) return false;
  const platform = getPlatform();
  const key = platform === "ios" ? IOS_KEY : ANDROID_KEY;
  if (!key) {
    logger.warn("[RevenueCat] Missing API key for", platform);
    return false;
  }
  try {
    const { Purchases } = await import("@revenuecat/purchases-capacitor");
    await Purchases.configure({ apiKey: key, appUserID: userId || null });
    _rcReady = true;
    return true;
  } catch (e) {
    logger.warn("[RevenueCat] configure failed", e);
    return false;
  }
};

export const linkNativeToBackend = async (api, userId) => {
  // Called by App bootstrap after login on native platforms
  if (!isNative()) return;
  const ok = await initRevenueCat(userId);
  if (!ok) return;
  try {
    const { Purchases } = await import("@revenuecat/purchases-capacitor");
    const info = await Purchases.getAppUserID();
    const rc_user_id = info?.appUserID || userId;
    await api.post("/billing/native/link", { rc_user_id, platform: getPlatform() });
  } catch (e) { logger.warn("[RevenueCat] link failed", e); }
};

export const buyPremium = async () => {
  if (!isNative() || !_rcReady) return { ok: false, reason: "not-native" };
  try {
    const { Purchases } = await import("@revenuecat/purchases-capacitor");
    const offerings = await Purchases.getOfferings();
    const pkg = offerings?.current?.availablePackages?.[0];
    if (!pkg) return { ok: false, reason: "no-offering" };
    const res = await Purchases.purchasePackage({ aPackage: pkg });
    return { ok: true, customerInfo: res.customerInfo };
  } catch (e) {
    return { ok: false, reason: "cancelled-or-failed", error: e };
  }
};

export const restorePurchases = async () => {
  if (!isNative() || !_rcReady) return { ok: false };
  try {
    const { Purchases } = await import("@revenuecat/purchases-capacitor");
    const res = await Purchases.restorePurchases();
    return { ok: true, customerInfo: res.customerInfo };
  } catch { return { ok: false }; }
};
